/**
 * Base class for all context objects like commands and mediators
 * Extended by: AbstractMediator, AbstractCommand
 * 
 * Contains convenience methods for the following:
 * - managing context event listeners and dispatching context events
 * - retrieving text from language pack
 * - managing delayed calls
 * - opening popups
 * - playing sounds
 * - tracking analytics events
 * 
 * Created on 11-apr-2017, 12:23:57
 * @author Reinier Feijen for &ranj
 **/

import GoogleAnalyticsEvent from "../../analytics/GoogleAnalyticsEvent";
import SkinFactory from "../ui/SkinFactory";
import SkinKey from "../ui/SkinKey";
import NavigateEvent from "../navigation/NavigateEvent";
import AnimationOptions from "../../../../lib/html5-software-library/src/ranj/ui/animation/AnimationOptions";
import SystemMessageEvent from "../system-message/model/SystemMessageEvent";
import SystemMessage from "../system-message/model/SystemMessage";

export default class AbstractContextObject {
    /**
     * @constructor
     * @param {soma.EventDispatcher} dispatcher 
     */
    constructor(dispatcher) {
        this._dispatcher = dispatcher;
    }

    /**
     * Convenience method for dispatching context events
     */
    dispatch(type, data) {
        this._dispatcher.dispatch(type, data);
    }

    /**
     * Convenience method for retrieving language pack texts
     * Requires: this._languagePack
     * @param {String} id 
     * @returns {String} text from language pack
     **/
    getText(id) {
        return this._languagePack.getText(id);
    }

    /**
     * Convenience method for opening popup
     * We use Tween for this instead of setTimeout, because Tween can be paused
     * Uses: NavigateCommandEvent, SkinFactory
     * @param {SkinKey} skinKey Identifier of skin
     * @param {Function} onClose Optional callback for closing popup
     * @param {{autoCenter: Boolean, priority: Number, needsOverlay: Boolean, openUnderCurrent: Boolean, animationType: String, animationOptions: AnimationOptions}} options Additional popup and animation options
     * @param {Object} skinData
     * @return {createjs.MovieClip} The skin that can be used to later close this screen
     */
    openPopup(skinKey, onClose, options, skinData) {
        options = options || {};

        let { autoCenter, priority, needsOverlay, openUnderCurrent, animationType, animationOptions } = options;

        var popup = SkinFactory.getSkin(skinKey);
        popup.onClose = onClose;
        popup.customData = skinData;
        //Method signature of NavigateCommandEvent constructor:
        //function(type, screenType, screen, autoCenter, priority, hasOverlay)
        var commandEvent = new NavigateEvent(NavigateEvent.POP_UP_REQUEST, null, popup, autoCenter, priority, needsOverlay, openUnderCurrent, animationType, animationOptions);
        this._dispatcher.dispatch(commandEvent.type, { data: commandEvent });

        return popup;
    }

    /**
     * Closes a popup
     * @param {SkinKey} skinKey Identifier of skin
     * @param {{ animationType: String, animationOptions: AnimationOptions}} options Additional popup and animation options
     */
    closePopup(skin, options) {
        options = options || {};

        let { animationType, animationOptions } = options;

        const commandEvent = new NavigateEvent(NavigateEvent.CLOSE_POP_UP_REQUEST, null, skin, null, null, null, null, animationType, animationOptions);
        this._dispatcher.dispatch(commandEvent.type, { data: commandEvent });
    }

    /**
     * Opens a screen and closes all remaining open screens
     * @param {SkinKey} skinKey
     */
    gotoScreen(skinKey, options, skinData) {
        options = options || {};

        let { animationType, animationOptions } = options;

        let screenSkin = SkinFactory.getSkin(skinKey);
        screenSkin.customData = skinData;

        const commandEvent = this.getGotoScreenRequest(null, screenSkin, null, null, null, null, animationType, animationOptions);
        this._dispatcher.dispatch(commandEvent.type, { data: commandEvent });

        return screenSkin;
    }

    getGotoScreenRequest(screenType, screen, autoCenter, priority, needsOverlay, openUnderCurrent, animationType, animationOptions) {
        return new NavigateEvent(NavigateEvent.GOTO_SCREEN_REQUEST, screenType, screen, autoCenter, priority, needsOverlay, openUnderCurrent, animationType, animationOptions);
    }

    /**
     * Opens a screen
     * @param {SkinKey} skinKey 
     * @param {{ animationType: String, animationOptions: AnimationOptions }} options 
     * @param {Object} skinData
     */
    openScreen(skinKey, options, skinData) {
        options = options || {};

        let { animationType, animationOptions } = options;

        let screenSkin = SkinFactory.getSkin(skinKey);
        screenSkin.customData = skinData;
        const commandEvent = this.getOpenScreenRequest(null, screenSkin, null, null, null, null, animationType, animationOptions);
        this._dispatcher.dispatch(commandEvent.type, { data: commandEvent });

        return screenSkin;
    }

    getOpenScreenRequest(screenType, screen, autoCenter, priority, needsOverlay, openUnderCurrent, animationType, animationOptions) {
        return new NavigateEvent(NavigateEvent.OPEN_SCREEN_REQUEST, screenType, screen, autoCenter, priority, needsOverlay, openUnderCurrent, animationType, animationOptions);
    }

    /**
     * Closes a screen
     * @param {SkinKey} skinKey 
     * @param {{ animationType: *, animationOptions: * }} options
     */
    closeScreen(screenSkin, options) {
        options = options || {};

        let { animationType, animationOptions } = options;

        const commandEvent = this.getCloseScreenRequest(null, screenSkin, null, null, null, null, animationType, animationOptions);
        this._dispatcher.dispatch(commandEvent.type, { data: commandEvent });
    }

    getCloseScreenRequest(screenType, screen, autoCenter, priority, needsOverlay, openUnderCurrent, animationType, animationOptions) {
        return new NavigateEvent(NavigateEvent.CLOSE_SCREEN_REQUEST, screenType, screen, autoCenter, priority, needsOverlay, openUnderCurrent, animationType, animationOptions);
    }

    /**
     * Convenience method for playing sound.
     * Requires: this._soundManager
     * NB!: soundManager is not injected into AbstractContextObject,
     *  but should be injected in subclasses.
     *  This method is part of AbstractContextObject, because a lot of mediators
     *  and commands have to play sounds, but we only want to inject the soundManager
     *  on a need-to-have basis...
     * @param {String} id 
     * @returns {String} id for soundinstance
     **/
    playSound(id, completeHandler, errorHandler, delay, offset, loop, volume, pan, fadeInDuration) {
        if (!this._soundManager) {
            console.error("AbstractContextObject.playSound: soundManager undefined. Please inject soundManager in baseclass.")
        }
        return this._soundManager.playSound(id, completeHandler, errorHandler, delay, offset, loop, volume, pan, fadeInDuration);
    }

    /**
     * Send Analytics tracking events
     * 
     * @param {string} name name of the Event
     * @param {{}} params object with the parameters for the Event (e.g., { screen_name: "splash", duration: 1234 })
     **/
    trackEvent(name, params = {}) {
        this._dispatcher.dispatch(GoogleAnalyticsEvent.EVENT, { name, params });
    }

    /**
     * Sets Analytics persistent value that is sent with every event
     * 
     * @param {string} name the name of the persistent value to be set
     * @param {string|number|boolean} value the persistent value to be set
     */
    trackPersistentValue(name, value) {
        this._dispatcher.dispatch(GoogleAnalyticsEvent.PERSISTENT_VALUE, { property: name, value });
    }
    
    /**
     * Sets Analytics User scope property - i.e., this value is sent once and is associated to the user
     * 
     * @param {string} name the name of the user property
     * @param {string|number|boolean} value the value for the user property
     */
    trackUserProperty(name, value) {
        this._dispatcher.dispatch(GoogleAnalyticsEvent.USER_PROPERTY, { property: name, value });
    }

    /**
     * Shorthand for showing a system message.
     * Tip: build the system message with the SystemMessageBuilder class
     * @param {SystemMessage} systemMessage 
     */
    showSystemMessage(systemMessage) {
        const evt = new SystemMessageEvent(SystemMessageEvent.OPEN, systemMessage);
        this.dispatch(evt.type, { data: evt });
    }
}